import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Breadcrumb } from '../components/Breadcrumb';
import { Card, DashboardLayout } from '../components';
import { printElementById } from '../utils/printDocument';
import {
  adminApi,
  type AdyenLedgerClassification,
  type AdyenLedgerQuery,
  type AdyenLedgerResponse,
  type AdyenLedgerRow,
  type AdyenReportImport
} from '../services/api';

type LinkedFilter = 'all' | 'linked' | 'unlinked';

const CLASSIFICATION_LABEL_KEY: Record<AdyenLedgerClassification, string> = {
  platform_fee: 'adminAdyenLedger.classifications.platform_fee',
  adyen_fee: 'adminAdyenLedger.classifications.adyen_fee',
  transaction_fee: 'adminAdyenLedger.classifications.transaction_fee',
  payout: 'adminAdyenLedger.classifications.payout',
  refund: 'adminAdyenLedger.classifications.refund',
  adjustment: 'adminAdyenLedger.classifications.adjustment',
  unknown: 'adminAdyenLedger.classifications.unknown'
};

const CLASSIFICATION_BADGE: Record<AdyenLedgerClassification, string> = {
  platform_fee: 'bg-primary',
  adyen_fee: 'bg-dark',
  transaction_fee: 'bg-info text-dark',
  payout: 'bg-success',
  refund: 'bg-warning text-dark',
  adjustment: 'bg-secondary',
  unknown: 'bg-light text-dark'
};

function formatOre(amountOre: number) {
  return new Intl.NumberFormat('sv-SE', {
    style: 'currency',
    currency: 'SEK'
  }).format(amountOre / 100);
}

function formatDate(value: string | null) {
  return value ? new Date(value).toLocaleDateString('sv-SE') : '—';
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function LedgerRows({ rows, onVoucher }: { rows: AdyenLedgerRow[]; onVoucher: (row: AdyenLedgerRow) => void }) {
  const { t } = useTranslation();
  return (
    <div className="table-responsive">
      <table className="table table-sm align-middle mb-0">
        <thead className="table-light">
          <tr>
            <th>{t('adminAdyenLedger.columns.accountingRef')}</th>
            <th>{t('adminAdyenLedger.columns.date')}</th>
            <th>{t('adminAdyenLedger.columns.type')}</th>
            <th>{t('adminAdyenLedger.columns.adyenRef')}</th>
            <th>{t('adminAdyenLedger.columns.psp')}</th>
            <th className="text-end">{t('adminAdyenLedger.columns.amount')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(row => (
            <tr key={row.id}>
              <td>
                <button
                  className="btn btn-link btn-sm p-0 font-monospace text-decoration-none"
                  type="button"
                  onClick={() => onVoucher(row)}
                >
                  {row.accountingReference || row.sourceId}
                </button>
              </td>
              <td>
                <small>
                  {formatDate(row.bookingDate)}
                  <span className="text-muted d-block">{t('adminAdyenLedger.columns.valueDate', { date: formatDate(row.valueDate) })}</span>
                </small>
              </td>
              <td>
                <span className={`badge ${CLASSIFICATION_BADGE[row.classification]}`}>
                  {t(CLASSIFICATION_LABEL_KEY[row.classification])}
                </span>
                <small className="text-muted d-block">{row.direction === 'incoming' ? t('adminAdyenLedger.direction.incoming') : t('adminAdyenLedger.direction.outgoing')}</small>
              </td>
              <td className="font-monospace small">{row.reference || row.transferId || '—'}</td>
              <td className="font-monospace small">{row.pspReference || '—'}</td>
              <td className={`text-end fw-semibold ${row.amountOre < 0 ? 'text-danger' : 'text-success'}`}>
                {formatOre(row.amountOre)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function AdminAdyenLedger() {
  const { t } = useTranslation();
  const [data, setData] = useState<AdyenLedgerResponse>({ rows: [], groups: [], unlinked: [] });
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [classification, setClassification] = useState<AdyenLedgerClassification | 'all'>('all');
  const [linked, setLinked] = useState<LinkedFilter>('all');
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<string | null>(null);
  const [voucherRow, setVoucherRow] = useState<AdyenLedgerRow | null>(null);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [reportName, setReportName] = useState('');
  const [reportBalanceAccountId, setReportBalanceAccountId] = useState('');
  const [reportText, setReportText] = useState('');
  const [selectedReportFile, setSelectedReportFile] = useState<{ name: string; size: number } | null>(null);
  const [importingReport, setImportingReport] = useState(false);
  const [reportImports, setReportImports] = useState<AdyenReportImport[]>([]);

  const query = useMemo<AdyenLedgerQuery>(() => ({
    fromDate: fromDate || undefined,
    toDate: toDate || undefined,
    classification,
    linked
  }), [classification, fromDate, linked, toDate]);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      adminApi.getAdyenLedger(query),
      adminApi.getAdyenLedgerReportImports()
    ])
      .then(([ledger, imports]) => {
        setData(ledger);
        setReportImports(imports);
      })
      .finally(() => setLoading(false));
  }, [query]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    return adminApi.subscribeAdyenLedgerEvents((event) => {
      setSyncResult(t('adminAdyenLedger.sync.result', { imported: event.data.imported, updated: event.data.updated, skipped: event.data.skipped }));
      load();
    });
  }, [load, t]);

  const totals = useMemo(() => {
    return data.rows.reduce(
      (acc, row) => {
        if (row.amountOre >= 0) acc.incoming += row.amountOre;
        else acc.outgoing += Math.abs(row.amountOre);
        return acc;
      },
      { incoming: 0, outgoing: 0 }
    );
  }, [data.rows]);

  const exportCsv = async () => {
    const result = await adminApi.exportAdyenLedger(query);
    downloadBlob(result.blob, result.filename);
  };

  const syncLedger = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const result = await adminApi.syncAdyenLedger({
        confirmation: 'SYNC_ADYEN_LEDGER',
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
        limit: 100
      });
      setSyncResult(t('adminAdyenLedger.sync.result', { imported: result.imported, updated: result.updated, skipped: result.skipped }));
      load();
    } finally {
      setSyncing(false);
    }
  };

  const importReport = async () => {
    setImportingReport(true);
    setSyncResult(null);
    try {
      const result = await adminApi.importAdyenLedgerReport({
        reportText,
        reportName: reportName || undefined,
        balanceAccountId: reportBalanceAccountId || undefined
      });
      setSyncResult(t('adminAdyenLedger.sync.result', { imported: result.imported, updated: result.updated, skipped: result.skipped }));
      setReportModalOpen(false);
      setReportText('');
      setSelectedReportFile(null);
      load();
    } finally {
      setImportingReport(false);
    }
  };

  const handleReportFile = async (file: File | null) => {
    if (!file) return;
    const text = await file.text();
    setReportText(text);
    setReportName(current => current || file.name);
    setSelectedReportFile({ name: file.name, size: file.size });
  };

  const printVoucher = () => {
    printElementById('adyen-ledger-voucher-print', t('adminAdyenLedger.voucher.title'));
  };

  return (
    <DashboardLayout>
      <div className="page-header mb-4">
        <div className="page-header-left d-flex align-items-center">
          <div className="page-header-title">
            <h5 className="fw-bold mb-0">{t('adminAdyenLedger.title')}</h5>
          </div>
          <Breadcrumb items={[{ label: t('adminAdyenLedger.breadcrumb.home'), path: '/admin' }, { label: t('adminAdyenLedger.breadcrumb.current') }]} />
        </div>
      </div>

      <div className="row g-3 mb-4">
        <div className="col-md-3">
          <div className="border rounded p-3 h-100">
            <small className="text-muted">{t('adminAdyenLedger.stats.rows')}</small>
            <div className="fw-bold fs-4 text-primary">{data.rows.length}</div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="border rounded p-3 h-100">
            <small className="text-muted">{t('adminAdyenLedger.stats.incoming')}</small>
            <div className="fw-bold fs-4 text-success">{formatOre(totals.incoming)}</div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="border rounded p-3 h-100">
            <small className="text-muted">{t('adminAdyenLedger.stats.outgoing')}</small>
            <div className="fw-bold fs-4 text-danger">{formatOre(totals.outgoing)}</div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="border rounded p-3 h-100">
            <small className="text-muted">{t('adminAdyenLedger.stats.unlinked')}</small>
            <div className="fw-bold fs-4 text-warning">{data.unlinked.length}</div>
          </div>
        </div>
      </div>

      <Card className="mb-4">
        <div className="d-flex flex-wrap align-items-end gap-2">
          <div>
            <label className="form-label small">{t('adminAdyenLedger.filters.from')}</label>
            <input className="form-control form-control-sm" type="date" value={fromDate} onChange={event => setFromDate(event.target.value)} />
          </div>
          <div>
            <label className="form-label small">{t('adminAdyenLedger.filters.to')}</label>
            <input className="form-control form-control-sm" type="date" value={toDate} onChange={event => setToDate(event.target.value)} />
          </div>
          <div>
            <label className="form-label small">{t('adminAdyenLedger.filters.classification')}</label>
            <select className="form-select form-select-sm" value={classification} onChange={event => setClassification(event.target.value as AdyenLedgerClassification | 'all')}>
              <option value="all">{t('adminAdyenLedger.filters.all')}</option>
              {Object.entries(CLASSIFICATION_LABEL_KEY).map(([value, labelKey]) => (
                <option key={value} value={value}>{t(labelKey)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="form-label small">{t('adminAdyenLedger.filters.linked')}</label>
            <select className="form-select form-select-sm" value={linked} onChange={event => setLinked(event.target.value as LinkedFilter)}>
              <option value="all">{t('adminAdyenLedger.filters.all')}</option>
              <option value="linked">{t('adminAdyenLedger.filters.businessLinked')}</option>
              <option value="unlinked">{t('adminAdyenLedger.filters.unlinked')}</option>
            </select>
          </div>
          <button className="btn btn-sm btn-outline-secondary" type="button" onClick={load} disabled={loading}>
            <i className="feather-refresh-cw me-1"></i>
            {t('adminAdyenLedger.actions.refresh')}
          </button>
          <button className="btn btn-sm btn-outline-primary" type="button" onClick={exportCsv} disabled={loading || data.rows.length === 0}>
            <i className="feather-download me-1"></i>
            {t('adminAdyenLedger.actions.exportCsv')}
          </button>
          <button className="btn btn-sm btn-primary" type="button" onClick={() => setReportModalOpen(true)}>
            <i className="feather-upload me-1"></i>
            {t('adminAdyenLedger.actions.importReport')}
          </button>
          <button className="btn btn-sm btn-outline-secondary" type="button" onClick={syncLedger} disabled={syncing}>
            <i className="feather-repeat me-1"></i>
            {t('adminAdyenLedger.actions.apiSync')}
          </button>
          {syncResult && <small className="text-muted">{syncResult}</small>}
        </div>
      </Card>

      {reportImports.length > 0 && (
        <Card className="mb-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="mb-0">{t('adminAdyenLedger.reportImports.title')}</h6>
            <small className="text-muted">{t('adminAdyenLedger.reportImports.latest', { count: reportImports.length })}</small>
          </div>
          <div className="table-responsive">
            <table className="table table-sm align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>{t('adminAdyenLedger.reportImports.columns.file')}</th>
                  <th>{t('adminAdyenLedger.reportImports.columns.status')}</th>
                  <th>{t('adminAdyenLedger.reportImports.columns.imported')}</th>
                  <th className="text-end">{t('adminAdyenLedger.reportImports.columns.rows')}</th>
                  <th>{t('adminAdyenLedger.reportImports.columns.checksum')}</th>
                </tr>
              </thead>
              <tbody>
                {reportImports.slice(0, 10).map(item => (
                  <tr key={item.id}>
                    <td>
                      <span className="font-monospace small">{item.fileName}</span>
                      <small className="text-muted d-block">
                        {item.reportType || t('adminAdyenLedger.reportImports.unknownType')} · {item.id.slice(0, 8)}
                      </small>
                      {item.reportReference && <small className="font-monospace text-muted d-block">{item.reportReference}</small>}
                      {item.errorMessage && <small className="text-danger d-block">{item.errorMessage}</small>}
                    </td>
                    <td>
                      <span className={`badge ${item.status === 'imported' ? 'bg-success' : item.status === 'failed' ? 'bg-danger' : 'bg-warning text-dark'}`}>
                        {item.status === 'imported' ? t('adminAdyenLedger.reportImports.status.imported') : item.status === 'failed' ? t('adminAdyenLedger.reportImports.status.failed') : t('adminAdyenLedger.reportImports.status.pending')}
                      </span>
                    </td>
                    <td><small>{item.importedAt ? new Date(item.importedAt).toLocaleString('sv-SE') : new Date(item.createdAt).toLocaleString('sv-SE')}</small></td>
                    <td className="text-end small">{t('adminAdyenLedger.reportImports.counts', { imported: item.importedCount, updated: item.updatedCount, skipped: item.skippedCount })}</td>
                    <td className="font-monospace small text-muted">{item.checksum?.slice(0, 12) ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {loading ? (
        <Card>
          <div className="text-center py-4">
            <div className="spinner-border spinner-border-sm text-primary" />
          </div>
        </Card>
      ) : (
        <>
          <div className="d-flex flex-column gap-3 mb-4">
            {data.groups.length === 0 && data.unlinked.length === 0 && (
              <Card>
                <div className="text-center text-muted py-4">{t('adminAdyenLedger.empty')}</div>
              </Card>
            )}
            {data.groups.map(group => (
              <Card key={group.projectId}>
                <div className="d-flex flex-wrap justify-content-between gap-2 mb-3">
                  <div>
                    <h6 className="mb-1">
                      {t('adminAdyenLedger.group.project', { number: group.projectNumber || '—' })} {group.projectDescription ? `· ${group.projectDescription}` : ''}
                    </h6>
                    <small className="text-muted">
                      {group.customerName || t('adminAdyenLedger.group.unknownCustomer')} · {group.companyName || t('adminAdyenLedger.group.unknownCompany')}
                    </small>
                  </div>
                  <div className="text-end">
                    <small className="text-muted d-block">{t('adminAdyenLedger.group.totalIncoming', { amount: formatOre(group.totalIncomingOre) })}</small>
                    <small className="text-muted d-block">{t('adminAdyenLedger.group.totalOutgoing', { amount: formatOre(group.totalOutgoingOre) })}</small>
                  </div>
                </div>
                <LedgerRows rows={group.rows} onVoucher={setVoucherRow} />
              </Card>
            ))}
          </div>

          {data.unlinked.length > 0 && (
            <Card>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0">{t('adminAdyenLedger.unlinkedRows')}</h6>
                <span className="badge bg-warning text-dark">{data.unlinked.length}</span>
              </div>
              <LedgerRows rows={data.unlinked} onVoucher={setVoucherRow} />
            </Card>
          )}
        </>
      )}

      {voucherRow && (
        <div className="modal d-block" tabIndex={-1} role="dialog" style={{ background: 'rgba(15, 23, 42, 0.45)' }}>
          <div className="modal-dialog modal-lg modal-dialog-scrollable" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{t('adminAdyenLedger.voucher.title')}</h5>
                <button type="button" className="btn-close" aria-label={t('adminAdyenLedger.actions.close')} onClick={() => setVoucherRow(null)} />
              </div>
              <div className="modal-body" id="adyen-ledger-voucher-print">
                <div className="border rounded p-3 mb-3">
                  <small className="text-muted">{t('adminAdyenLedger.voucher.accountingReference')}</small>
                  <div className="font-monospace fw-semibold text-break">{voucherRow.accountingReference || voucherRow.sourceId}</div>
                </div>
                <div className="row g-3">
                  <div className="col-md-6">
                    <dl className="mb-0">
                      <dt>{t('adminAdyenLedger.voucher.bookingDate')}</dt>
                      <dd>{formatDate(voucherRow.bookingDate)}</dd>
                      <dt>{t('adminAdyenLedger.voucher.valueDate')}</dt>
                      <dd>{formatDate(voucherRow.valueDate)}</dd>
                      <dt>{t('adminAdyenLedger.voucher.classification')}</dt>
                      <dd>{t(CLASSIFICATION_LABEL_KEY[voucherRow.classification])}</dd>
                      <dt>{t('adminAdyenLedger.voucher.amount')}</dt>
                      <dd className={voucherRow.amountOre < 0 ? 'text-danger' : 'text-success'}>{formatOre(voucherRow.amountOre)}</dd>
                    </dl>
                  </div>
                  <div className="col-md-6">
                    <dl className="mb-0">
                      <dt>{t('adminAdyenLedger.voucher.deal')}</dt>
                      <dd>
                        {voucherRow.projectNumber
                          ? `${t('adminAdyenLedger.voucher.project', { number: voucherRow.projectNumber })}${voucherRow.projectDescription ? ` · ${voucherRow.projectDescription}` : ''}`
                          : t('adminAdyenLedger.voucher.unlinked')}
                      </dd>
                      <dt>{t('adminAdyenLedger.voucher.customer')}</dt>
                      <dd>
                        {voucherRow.customerName || '—'}
                        {voucherRow.customerNumber ? <span className="text-muted"> · {t('adminAdyenLedger.voucher.customerNumber', { number: voucherRow.customerNumber })}</span> : null}
                      </dd>
                      <dt>{t('adminAdyenLedger.voucher.company')}</dt>
                      <dd>
                        {voucherRow.companyName || '—'}
                        {voucherRow.companyNumber ? <span className="text-muted"> · {t('adminAdyenLedger.voucher.companyNumber', { number: voucherRow.companyNumber })}</span> : null}
                      </dd>
                      <dt>{t('adminAdyenLedger.voucher.contract')}</dt>
                      <dd>{voucherRow.contractNumber ? `#${voucherRow.contractNumber}` : '—'}</dd>
                      <dt>{t('adminAdyenLedger.voucher.milestone')}</dt>
                      <dd>{voucherRow.milestoneNumber ? `#${voucherRow.milestoneNumber}` : '—'}</dd>
                    </dl>
                  </div>
                </div>
                <hr />
                <dl className="mb-0">
                  <dt>{t('adminAdyenLedger.voucher.balanceAccount')}</dt>
                  <dd className="font-monospace text-break">{voucherRow.balanceAccountId}</dd>
                  <dt>{t('adminAdyenLedger.voucher.reference')}</dt>
                  <dd className="font-monospace text-break">{voucherRow.reference || '—'}</dd>
                  <dt>{t('adminAdyenLedger.voucher.pspReference')}</dt>
                  <dd className="font-monospace text-break">{voucherRow.pspReference || '—'}</dd>
                  <dt>{t('adminAdyenLedger.voucher.transferId')}</dt>
                  <dd className="font-monospace text-break">{voucherRow.transferId || '—'}</dd>
                  <dt>{t('adminAdyenLedger.voucher.sourceRow')}</dt>
                  <dd className="font-monospace text-break">{voucherRow.sourceType}:{voucherRow.sourceId}</dd>
                </dl>
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-secondary" type="button" onClick={() => setVoucherRow(null)}>{t('adminAdyenLedger.actions.close')}</button>
                <button className="btn btn-primary" type="button" onClick={printVoucher}>
                  <i className="feather-printer me-1"></i>
                  {t('adminAdyenLedger.actions.print')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {reportModalOpen && (
        <div className="modal d-block" tabIndex={-1} role="dialog" style={{ background: 'rgba(15, 23, 42, 0.45)' }}>
          <div className="modal-dialog modal-lg modal-dialog-scrollable" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{t('adminAdyenLedger.reportModal.title')}</h5>
                <button type="button" className="btn-close" aria-label={t('adminAdyenLedger.actions.close')} onClick={() => setReportModalOpen(false)} />
              </div>
              <div className="modal-body">
                <div className="row g-3 mb-3">
                  <div className="col-md-6">
                    <label className="form-label small">{t('adminAdyenLedger.reportModal.nameLabel')}</label>
                    <input
                      className="form-control form-control-sm"
                      value={reportName}
                      onChange={event => setReportName(event.target.value)}
                      placeholder="Balance Platform Accounting Report"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small">{t('adminAdyenLedger.reportModal.balanceAccountLabel')}</label>
                    <input
                      className="form-control form-control-sm font-monospace"
                      value={reportBalanceAccountId}
                      onChange={event => setReportBalanceAccountId(event.target.value)}
                      placeholder={t('adminAdyenLedger.reportModal.balanceAccountPlaceholder')}
                    />
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label small">{t('adminAdyenLedger.reportModal.fileLabel')}</label>
                  <input
                    className="form-control form-control-sm"
                    type="file"
                    accept=".csv,.tsv,text/csv,text/tab-separated-values,text/plain"
                    onChange={event => {
                      handleReportFile(event.target.files?.[0] ?? null);
                      event.target.value = '';
                    }}
                  />
                  {selectedReportFile && (
                    <small className="text-muted d-block mt-1">
                      {selectedReportFile.name} · {(selectedReportFile.size / 1024).toLocaleString('sv-SE', { maximumFractionDigits: 1 })} kB
                    </small>
                  )}
                </div>
                <label className="form-label small">{t('adminAdyenLedger.reportModal.contentLabel')}</label>
                <textarea
                  className="form-control font-monospace small"
                  rows={16}
                  value={reportText}
                  onChange={event => setReportText(event.target.value)}
                  placeholder={t('adminAdyenLedger.reportModal.contentPlaceholder')}
                />
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-secondary" type="button" onClick={() => setReportModalOpen(false)}>{t('adminAdyenLedger.actions.close')}</button>
                <button className="btn btn-primary" type="button" onClick={importReport} disabled={importingReport || !reportText.trim()}>
                  <i className="feather-upload me-1"></i>
                  {t('adminAdyenLedger.actions.import')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
